import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineOverlappingGraph from "renderers/multi-line-overlapping-graph"
import { createCompositionMultiDateQuery, createCompositionIntradayBatchTrendQueries } from "gatherers/multi-date/composition"
import { POSITIONS_COUNT, MAX_CONCENTRATION, TOP_CONCENTRATION } from "gatherers/constants"
import { filterItemsWithData, extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { autoFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { TOP_POSITION_COUNT, CONCENTRATION_FACTORS } from "helpers/analyzeConstants"
import type { ModelMetadata } from "resources/models"
import { useIntradayState, useIntradayBatchTrendMode } from "connected-components/dashboard/hooks/use-intraday-state"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { useGraphData } from "../../hooks/use-graph-data"

type ConcentrationFactorId = keyof typeof CONCENTRATION_FACTORS

interface CompositionItem {
  id: ConcentrationFactorId
  label: string
  disabled: boolean
  color: string
  area?: string
}

interface CompositionDataPoint {
  date: string
  [key: string]: unknown
}

const SERIES: { id: ConcentrationFactorId, label: string }[] = [
  { id: POSITIONS_COUNT, label: "Positions" },
  { id: MAX_CONCENTRATION, label: "Max Concentration" },
  { id: TOP_CONCENTRATION, label: `Top ${TOP_POSITION_COUNT} Concentration` },
]

const TOOLTIP_FORMATTERS = {
  [MAX_CONCENTRATION]: percentageFormatter,
  [TOP_CONCENTRATION]: percentageFormatter,
  [POSITIONS_COUNT]: (value: number) => value,
}

const CONCENTRATION_KEYS = [MAX_CONCENTRATION, TOP_CONCENTRATION]

// MultiLineOverlapping's breakdown axis always reads its domain as a decimal fraction, so the gatherer's already-scaled values (4.2 meaning 4.2%) need rescaling before they reach the chart.
function toDecimalConcentration(dates: CompositionDataPoint[]): CompositionDataPoint[] {
  return dates.map((point) => {
    const converted = { ...point }
    for (const key of CONCENTRATION_KEYS) {
      if (typeof converted[key] === "number") {
        converted[key] = (converted[key] as number) / 100
      }
    }
    return converted
  })
}

function extractCompositionData(data: unknown[]): CompositionDataPoint[] {
  return toDecimalConcentration(extractDatesFromFirstSeries(data))
}

function extractBatchTrendCompositionData(data: unknown[]): CompositionDataPoint[] {
  return toDecimalConcentration(extractDatesFromEachSeries(data))
}

interface CompositionGraphProps {
  modelMetadata: ModelMetadata
}

export default function CompositionGraph({ modelMetadata }: CompositionGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
    instrumentsMetadata,
  } = useGraphData()

  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id
  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDateClick = useCallback((dateString: string, clickPosition: ClickPosition | undefined) => {
    if (!panelId) return
    const clickedBatch = isBatchTrendMode ? completedBatches.find(batch => batch.id === clickPosition?.batchId) : undefined
    const snapshotDate = clickedBatch?.date ?? dateString
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.composition",
      title: "Asset View: Composition",
      view: "composition",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A day-based point has no clickedBatch even in batch-trend mode; falling back to intradayBatchMetadata
        // keeps it pinned to that historical snapshot instead of routing through the live/unpinned batch path.
        ...resolveIntradayDrilldownFields(isIntradayMode, clickedBatch ?? intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        clickedDate: snapshotDate,
      },
    }))
  }, [panelId, openDrilldown, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter ?? undefined, normalization)
  const segment = useFilteredSegment(portfolioFilter ?? undefined, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  // Colors come from Analyze's own CONCENTRATION_FACTORS, pinned by declared order — filterItemsWithData
  // assigns colors after a magnitude sort, which would swap them between portfolios otherwise.
  const currentItems = useMemo(
    () => SERIES.map(series => ({ ...series, color: CONCENTRATION_FACTORS[series.id].color, disabled: Boolean(disabledItems[series.id]) })),
    [disabledItems],
  )

  const filterItems = useCallback(
    (items: CompositionItem[], data: CompositionDataPoint[]) =>
      filterItemsWithData(items, data).map((item: CompositionItem) =>
        item.id !== POSITIONS_COUNT
          ? { ...item, area: CONCENTRATION_FACTORS[item.id].area }
          : item,
      ),
    [],
  )

  const gatherers = useMemo(() => {
    const dayBasedQuery = createCompositionMultiDateQuery(modelMetadata.id, instrumentWithSegment, baseWithSegment, startDate, endDate)
    if (!isBatchTrendMode) return [dayBasedQuery]
    return [dayBasedQuery, ...createCompositionIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches)]
  }, [modelMetadata.id, instrumentWithSegment, baseWithSegment, startDate, endDate, isBatchTrendMode, completedBatches])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const isIntradayEligible = isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId)
  const intradayUnavailableMessage = isIntradayEligible && !isBatchTrendMode
    ? (instrumentsMetadata?.original?.intradayEnabled
      ? "Composition updates when today's first snapshot completes."
      : "Intraday composition isn't available with live pricing.")
    : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractBatchTrendCompositionData : extractCompositionData}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineOverlappingGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDateClick: handleDateClick,
      totalLabel: "Positions",
      breakdownLabel: "Concentration",
      totalFormatter: autoFormatter,
      breakdownFormatter: percentageFormatter,
      tooltipFormatters: TOOLTIP_FORMATTERS,
      historicalOnlyMessage: intradayUnavailableMessage,
      loadingMessage: "Loading composition data...",
      errorMessage: "Failed to load composition data. Please try again.",
      emptyMessage: "No composition data available.",
      noDataMessage: intradayUnavailableMessage,
      isTruncated,
    }}
  />
}
